import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { subscribeNewsletter } from "../lib/api";

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      setDone(true);
      toast.success(res.message || "Welcome aboard.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative bg-[#FDFBF7] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <p className="kicker">The Dispatch</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mt-5 text-stone-900">
            Travel letters,
            <br /><em className="italic">once a month.</em>
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed text-[15px] max-w-xl">
            A slow-read dispatch from wherever we've parked the suitcases this month. Cliff-top villages, month-long kitchens, the occasional Bell's palsy story. No spam, no ads, unsubscribe at any time.
          </p>
        </div>

        <div className="md:col-span-5">
          {!done ? (
            <motion.form
              onSubmit={onSubmit}
              data-testid="newsletter-form"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col gap-5"
            >
              <label className="block">
                <span className="kicker text-stone-500">Your email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  data-testid="newsletter-email-input"
                  className="input-editorial mt-3"
                  required
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="group inline-flex items-center justify-center gap-3 bg-stone-900 text-[#FDFBF7] px-8 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-[#9c4122] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Subscribe"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-xs text-stone-500 tracking-wide">We'll never share your email. Ever.</p>
            </motion.form>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="newsletter-success" className="border border-stone-300 p-8 bg-white">
              <Check className="text-[#9c4122]" size={28} />
              <h3 className="font-serif text-2xl mt-4">Welcome aboard.</h3>
              <p className="mt-3 text-stone-600 text-[15px] leading-relaxed">
                You're on the list. Our next dispatch will find you soon — likely from somewhere with better light than your inbox.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
